import { useState, useEffect, useRef } from 'react';
import {
    Avatar, CreateAvatarDto, UpdateAvatarDto,
    fetchAllAvatars, createAvatar, updateAvatar, deleteAvatar,
    uploadFile,
} from '../api/kokki';

const C = { primary: '#10b981', secondary: '#3b82f6', danger: '#ef4444' };
const inputStyle: React.CSSProperties = { width: '100%', padding: '12px', background: '#0f172a', border: '1px solid #334155', borderRadius: '10px', color: '#f1f5f9', fontSize: '14px', boxSizing: 'border-box' };
const labelStyle: React.CSSProperties = { color: '#94a3b8', fontSize: '12px', marginBottom: '6px', display: 'block', fontWeight: '600' };
const btnStyle = (bg: string, color = '#fff'): React.CSSProperties => ({ padding: '10px 18px', background: bg, color, border: 'none', borderRadius: '10px', fontSize: '13px', cursor: 'pointer', fontWeight: '600' });

const resolveUrl = (url: string | null | undefined) => {
    if (!url) return '';
    if (url.startsWith('/')) return `${(process.env.REACT_APP_API_URL || 'https://api.playkokki.com').replace(/\/$/, '')}${url}`;
    return url;
};

const ImageUploadButton = ({ onUploaded, label = 'Upload Image' }: { onUploaded: (url: string) => void; label?: string }) => {
    const ref = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try { setUploading(true); const { url } = await uploadFile(file); onUploaded(url); }
        catch { alert('Upload failed'); }
        finally { setUploading(false); if (ref.current) ref.current.value = ''; }
    };
    return (
        <>
            <input ref={ref} type="file" accept="image/*,.gif,.svg" onChange={handleFile} style={{ display: 'none' }} />
            <button type="button" onClick={() => ref.current?.click()} disabled={uploading} style={{ ...btnStyle(uploading ? '#334155' : C.secondary), fontSize: '12px', padding: '8px 14px' }}>
                {uploading ? '⏳ Uploading...' : `📁 ${label}`}
            </button>
        </>
    );
};

const DeleteModal = ({ name, onConfirm, onClose, loading }: { name: string; onConfirm: () => void; onClose: () => void; loading: boolean }) => (
    <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '24px' }}>
        <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '20px', border: '1px solid #334155', width: '100%', maxWidth: '450px', padding: '32px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🗑️</div>
            <h2 style={{ color: '#f1f5f9', fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>Delete Avatar?</h2>
            <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '24px' }}>Are you sure you want to delete "<span style={{ color: '#f1f5f9' }}>{name}</span>"? This cannot be undone.</p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button onClick={onClose} style={btnStyle('#334155', '#f1f5f9')}>Cancel</button>
                <button onClick={onConfirm} disabled={loading} style={btnStyle(loading ? '#334155' : C.danger)}>{loading ? 'Deleting...' : 'Delete'}</button>
            </div>
        </div>
    </div>
);

const AvatarFormModal = ({ avatar, onSave, onClose, loading }: { avatar?: Avatar | null; onSave: (d: CreateAvatarDto | UpdateAvatarDto) => void; onClose: () => void; loading: boolean }) => {
    const [name, setName] = useState(avatar?.name || '');
    const [imageUrl, setImageUrl] = useState(avatar?.imageUrl || '');
    const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); onSave({ name, imageUrl }); };
    return (
        <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '24px' }}>
            <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '20px', border: '1px solid #334155', width: '100%', maxWidth: '550px', padding: '32px' }}>
                <h2 style={{ color: '#f1f5f9', fontSize: '20px', fontWeight: '700', marginBottom: '20px' }}>{avatar ? '✏️ Edit Avatar' : '➕ Create Avatar'}</h2>
                <form onSubmit={handleSubmit}>
                    <div style={{ display: 'grid', gap: '16px' }}>
                        <div>
                            <label style={labelStyle}>Name *</label>
                            <input value={name} onChange={(e) => setName(e.target.value)} required style={inputStyle} placeholder="e.g. Cool Cat, Space Ranger" />
                        </div>
                        <div>
                            <label style={labelStyle}>Avatar Image</label>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder="URL or upload" />
                                <ImageUploadButton onUploaded={setImageUrl} />
                            </div>
                            {imageUrl && <img src={resolveUrl(imageUrl)} alt="preview" style={{ marginTop: '10px', width: '80px', height: '80px', objectFit: 'cover', borderRadius: '12px', border: '2px solid #334155' }} onError={(e) => (e.currentTarget.style.display = 'none')} />}
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                        <button type="button" onClick={onClose} style={btnStyle('#334155', '#f1f5f9')}>Cancel</button>
                        <button type="submit" disabled={loading || !name} style={btnStyle(loading || !name ? '#334155' : C.primary, loading || !name ? '#64748b' : '#0f172a')}>
                            {loading ? 'Saving...' : avatar ? 'Update' : 'Create'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default function AvatarsPage() {
    const [avatars, setAvatars] = useState<Avatar[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [selected, setSelected] = useState<Avatar | null>(null);

    const load = async () => {
        try { setLoading(true); setError(null); setAvatars(await fetchAllAvatars()); }
        catch (e: any) { setError(e.message); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);
    const flash = (msg: string) => { setSuccess(msg); setTimeout(() => setSuccess(null), 3000); };

    const handleSave = async (data: CreateAvatarDto | UpdateAvatarDto) => {
        try {
            setSubmitting(true); setError(null);
            if (selected) { await updateAvatar(selected.id, data); flash('Avatar updated!'); }
            else { await createAvatar(data as CreateAvatarDto); flash('Avatar created!'); }
            setShowForm(false); load();
        } catch (e: any) { setError(e.message); } finally { setSubmitting(false); }
    };

    const handleConfirmDelete = async () => {
        if (!selected) return;
        try {
            setSubmitting(true); setError(null);
            await deleteAvatar(selected.id);
            flash('Avatar deleted!'); setShowDelete(false); load();
        } catch (e: any) { setError(e.message); } finally { setSubmitting(false); }
    };

    return (
        <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)', padding: '32px' }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                    <div>
                        <h1 style={{ color: '#f1f5f9', fontSize: '32px', fontWeight: '800', marginBottom: '8px' }}>🧑‍🎨 User Avatars</h1>
                        <p style={{ color: '#64748b', fontSize: '14px' }}>{avatars.length} avatar{avatars.length !== 1 ? 's' : ''} • User profile avatars</p>
                    </div>
                    <button onClick={() => { setSelected(null); setShowForm(true); }} style={{ padding: '12px 20px', background: C.primary, color: '#0f172a', border: 'none', borderRadius: '12px', fontSize: '14px', cursor: 'pointer', fontWeight: '600' }}>➕ Add Avatar</button>
                </div>

                {success && <div style={{ background: '#064e3b', border: '1px solid #10b981', padding: '16px 20px', borderRadius: '12px', color: '#10b981', fontSize: '14px', marginBottom: '24px' }}>✅ {success}</div>}
                {error && <div style={{ background: '#7f1d1d', border: '1px solid #ef4444', padding: '16px 20px', borderRadius: '12px', color: '#fca5a5', fontSize: '14px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between' }}><span>❌ {error}</span><button onClick={() => setError(null)} style={{ background: 'transparent', border: 'none', color: '#fca5a5', fontSize: '18px', cursor: 'pointer' }}>×</button></div>}

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>⏳ Loading avatars...</div>
                ) : avatars.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px' }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🧑‍🎨</div>
                        <div style={{ color: '#64748b', fontSize: '16px', marginBottom: '24px' }}>No avatars yet</div>
                        <button onClick={() => { setSelected(null); setShowForm(true); }} style={btnStyle(C.primary, '#0f172a')}>Create your first Avatar</button>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '20px' }}>
                        {avatars.map(a => (
                            <div key={a.id} style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '16px', border: '1px solid #334155', padding: '20px', textAlign: 'center' }}>
                                {a.imageUrl ? <img src={resolveUrl(a.imageUrl)} alt={a.name} style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #334155', margin: '0 auto 12px' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <div style={{ width: '80px', height: '80px', background: '#334155', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '32px', margin: '0 auto 12px' }}>🧑</div>}
                                <h3 style={{ color: '#f1f5f9', fontSize: '16px', fontWeight: '700', marginBottom: '12px' }}>{a.name}</h3>
                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                    <button onClick={() => { setSelected(a); setShowForm(true); }} style={btnStyle(C.secondary)}>✏️ Edit</button>
                                    <button onClick={() => { setSelected(a); setShowDelete(true); }} style={btnStyle(C.danger)}>🗑️ Delete</button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {showForm && <AvatarFormModal avatar={selected} onSave={handleSave} onClose={() => setShowForm(false)} loading={submitting} />}
            {showDelete && selected && <DeleteModal name={selected.name} onConfirm={handleConfirmDelete} onClose={() => setShowDelete(false)} loading={submitting} />}
        </div>
    );
}
